import { apiClient } from './client'
import type {
  AllocateBankLineInput,
  BankAccountDto,
  BankAutoAllocateResultDto,
  BankLineCandidatesDto,
  BankLineDto,
  InvoiceRegistryResultDto,
  InvoiceRegistryRow,
  BankStatementDetailDto,
  BankSettingsDto,
  BankStatementDto,
  SellerEntity,
  UpdateBankSettingsResultDto,
} from '@crm/shared'

/**
 * Раздел «Банк» (этап 3, Б2, docs/features/client-payments.md): бухгалтер и
 * администратор загружают выписки и разбирают строки, генеральный директор смотрит.
 */

type Envelope<T> = { success: true; data: T }

export interface UploadBankStatementResult {
  /** null — новых строк в файле нет. */
  statementId: string | null
  accountNumber: string
  linesTotal: number
  linesNew: number
  entityMissing: boolean
  /** Сколько новых поступлений программа разнесла по заказам сама. */
  autoAllocated: number
  /** Сколько новых поступлений ушло в «не учитываем»: счёт до даты начала учёта. */
  beforeStart: number
}

export async function getBankStatements(): Promise<BankStatementDto[]> {
  const res = await apiClient.get<Envelope<BankStatementDto[]>>('/bank/statements')
  return res.data.data
}

export async function getBankStatement(id: string): Promise<BankStatementDetailDto> {
  const res = await apiClient.get<Envelope<BankStatementDetailDto>>(`/bank/statements/${id}`)
  return res.data.data
}

export async function uploadBankStatement(
  fileName: string,
  content: string,
  /** Юрлицо расчётного счёта — для счёта, у которого оно ещё не указано. */
  entityId?: SellerEntity,
): Promise<UploadBankStatementResult> {
  const res = await apiClient.post<Envelope<UploadBankStatementResult>>('/bank/statements', {
    fileName,
    content,
    ...(entityId ? { entityId } : {}),
  })
  return res.data.data
}

export async function getBankAccounts(): Promise<BankAccountDto[]> {
  const res = await apiClient.get<Envelope<BankAccountDto[]>>('/bank/accounts')
  return res.data.data
}

export async function updateBankAccount(
  id: string,
  entityId: SellerEntity | null,
): Promise<BankAccountDto> {
  const res = await apiClient.patch<Envelope<BankAccountDto>>(`/bank/accounts/${id}`, { entityId })
  return res.data.data
}

export async function updateBankLine(
  id: string,
  status: 'PENDING' | 'SKIPPED',
): Promise<BankLineDto> {
  const res = await apiClient.patch<Envelope<BankLineDto>>(`/bank/lines/${id}`, { status })
  return res.data.data
}

/** Очередь «не разнесено»: открытые поступления всех выписок. */
export async function getOpenBankLines(): Promise<BankLineDto[]> {
  const res = await apiClient.get<Envelope<BankLineDto[]>>('/bank/lines')
  return res.data.data
}

/** Данные окна разноса; `clientId` — клиент, выбранный вручную. */
export async function getBankLineCandidates(
  id: string,
  clientId?: string,
): Promise<BankLineCandidatesDto> {
  const res = await apiClient.get<Envelope<BankLineCandidatesDto>>(`/bank/lines/${id}/candidates`, {
    params: clientId ? { clientId } : undefined,
  })
  return res.data.data
}

export async function allocateBankLine(
  id: string,
  input: AllocateBankLineInput,
): Promise<BankLineDto> {
  const res = await apiClient.post<Envelope<BankLineDto>>(`/bank/lines/${id}/allocate`, input)
  return res.data.data
}

export async function unallocateBankLine(id: string): Promise<BankLineDto> {
  const res = await apiClient.post<Envelope<BankLineDto>>(`/bank/lines/${id}/unallocate`)
  return res.data.data
}

export async function autoAllocateBankLines(
  statementId?: string,
): Promise<BankAutoAllocateResultDto> {
  const res = await apiClient.post<Envelope<BankAutoAllocateResultDto>>('/bank/auto-allocate', {
    ...(statementId ? { statementId } : {}),
  })
  return res.data.data
}

/** Реестр счетов 1С (Б4): номера счетов из 1С — в счета заказов. */
export async function uploadInvoiceRegistry(
  rows: InvoiceRegistryRow[],
): Promise<InvoiceRegistryResultDto> {
  const res = await apiClient.post<Envelope<InvoiceRegistryResultDto>>('/bank/invoice-registry', {
    rows,
  })
  return res.data.data
}

/** Ручной выбор: записать номер из реестра в счёт заказа. */
export async function assignInvoiceNumber(
  invoiceId: string,
  number: string,
): Promise<{ autoAllocated: number }> {
  const res = await apiClient.post<Envelope<{ autoAllocated: number }>>(
    '/bank/invoice-registry/assign',
    { invoiceId, number },
  )
  return res.data.data
}

/** Удаляет выписку: разнос её строк отменяется, строки удаляются. */
export async function deleteBankStatement(
  id: string,
): Promise<{ lines: number; unallocated: number }> {
  const res = await apiClient.delete<Envelope<{ lines: number; unallocated: number }>>(
    `/bank/statements/${id}`,
  )
  return res.data.data
}

/** Дата начала учёта: оплаты по счетам до неё — «не учитываем». */
export async function getBankSettings(): Promise<BankSettingsDto> {
  const res = await apiClient.get<Envelope<BankSettingsDto>>('/bank/settings')
  return res.data.data
}

export async function updateBankSettings(
  accountingStartDate: string | null,
): Promise<UpdateBankSettingsResultDto> {
  const res = await apiClient.put<Envelope<UpdateBankSettingsResultDto>>('/bank/settings', {
    accountingStartDate,
  })
  return res.data.data
}
